// ============================================================
// MULTI-TENANT: src/hooks/useTenantAuth.js
//
// FASE 3: Migrado para Supabase Auth
//  - signIn usa supabase.auth.signInWithPassword() + RPC buscar_perfil_usuario()
//  - signOut usa supabase.auth.signOut() (JWT invalidado no servidor)
//  - sessionStorage removido — sessão gerenciada pelo SDK do Supabase
//
// Fluxo multi-tenant preservado:
//  - Email em 1 empresa  → loga direto, retorna { ok: true, usuario }
//  - Email em 2+ empresas → retorna { ok: false, empresas: [...] }
// ============================================================

import { useState, useCallback } from 'react';
import { useTenant } from '../contexts/TenantContext';
import useAppStore from '../store/useAppStore';
import supabase from '../services/supabaseClient';
import { getTenantById } from '../services/tenantService';
import { logLogin, logLogout } from '../utils/auditoria';

export function useTenantAuth() {
  const { setTenantData, clearTenantData } = useTenant();
  const { setUsuario, clearUsuario, setTenant, clearTenant } = useAppStore();

  const [carregando, setCarregando] = useState(false);
  const [erro,       setErro]       = useState('');

  // ── Helper: finaliza login após autenticação bem-sucedida ─
  const _finalizarLogin = useCallback(async (u, tenant) => {
    // Atualiza último acesso
    await supabase
      .from('usuarios')
      .update({ ultimo_acesso: new Date().toISOString() })
      .eq('id', u.id);

    // Atualiza Context + Zustand (sessionStorage não é mais necessário —
    // a sessão real está no JWT gerenciado pelo supabase.auth)
    setTenantData(tenant);
    setUsuario(u);
    setTenant(tenant);

    await logLogin(tenant?.id ?? null);
    return u;
  }, [setTenantData, setUsuario, setTenant]);

  // ── Helper: super admin escolhe em qual empresa entrar ──
  // Sem empresa escolhida o tenantId fica nulo, e toda tela que filtra por
  // tenant_id (Atendimento, Dashboard, clientes...) vem vazia. A lista sai das
  // empresas ativas com algum módulo do CRM: quem só usa o Finance fica fora.
  const _empresasDoSuperAdmin = useCallback(async (u) => {
    const { data, error } = await supabase.from('tenants').select('*').eq('ativo', true).order('nome');
    if (error) throw new Error('Erro ao carregar as empresas. Contate o suporte.');
    const empresas = (data || [])
      .filter((t) => !Array.isArray(t.modulos) || t.modulos.length > 0)
      .map((tenant) => ({ usuario: u, tenant }));
    if (empresas.length === 1) {
      const usuario = await _finalizarLogin(u, empresas[0].tenant);
      return { ok: true, usuario };
    }
    return { ok: false, empresas };
  }, [_finalizarLogin]);

  // ── Helper: loga perfil único, resolvendo tenant quando necessário ──
  const _loginPerfilUnico = useCallback(async (u) => {
    if (u.role === 'super_admin') return _empresasDoSuperAdmin(u);
    if (!u.tenant_id) throw new Error('Usuário sem empresa vinculada. Contate o suporte.');
    const tenant = await getTenantById(u.tenant_id);
    if (!tenant) throw new Error('Empresa não encontrada ou inativa. Contate o suporte.');
    const usuario = await _finalizarLogin(u, tenant);
    return { ok: true, usuario };
  }, [_finalizarLogin, _empresasDoSuperAdmin]);

  // ── Helper: resolve múltiplos perfis → empresa única ou seletor ──
  const _resolverPerfis = useCallback(async (perfis) => {
    const superAdmin = perfis.find((p) => p.role === 'super_admin');
    if (superAdmin) return _empresasDoSuperAdmin(superAdmin);
    const lista = await Promise.all(
      perfis.map(async (u) => {
        const tenant = u.tenant_id ? await getTenantById(u.tenant_id) : null;
        return tenant ? { usuario: u, tenant } : null;
      })
    );
    const empresasValidas = lista.filter(Boolean);
    if (empresasValidas.length === 1) {
      const { usuario: u, tenant } = empresasValidas[0];
      const usuario = await _finalizarLogin(u, tenant);
      return { ok: true, usuario };
    }
    return { ok: false, empresas: empresasValidas };
  }, [_finalizarLogin, _empresasDoSuperAdmin]);

  // ── Login principal ───────────────────────────────────────
  // Retorna:
  //   { ok: true,  usuario }                 → logado direto
  //   { ok: false, erro: string }            → credenciais erradas
  //   { ok: false, empresas: [...] }         → múltiplas empresas, aguarda escolha
  const signIn = useCallback(async (email, senha) => {
    setErro('');
    setCarregando(true);
    try {
      // 1. Autentica via Supabase Auth (JWT real)
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password: senha });
      if (authError) {
        const msg = authError.message.includes('Invalid login credentials')
          ? 'Email ou senha incorretos'
          : 'Erro ao conectar ao servidor';
        throw new Error(msg);
      }

      // 2. Busca perfil(s) do usuário autenticado (RPC usa auth.uid())
      const { data: perfis, error: rpcError } = await supabase.rpc('buscar_perfil_usuario');
      if (rpcError) throw new Error('Erro ao carregar perfil. Contate o suporte.');
      if (!perfis || perfis.length === 0) {
        await supabase.auth.signOut();
        throw new Error('Usuário não encontrado ou inativo. Contate o suporte.');
      }

      // 3. Despacha para o helper adequado
      if (perfis.length === 1) return _loginPerfilUnico(perfis[0]);
      return _resolverPerfis(perfis);

    } catch (err) {
      setErro(err.message);
      return { ok: false };
    } finally {
      setCarregando(false);
    }
  }, [_loginPerfilUnico, _resolverPerfis]);

  // ── Chamado quando usuário seleciona a empresa no seletor ─
  const signInComEmpresa = useCallback(async (u, tenant) => {
    setCarregando(true);
    try {
      const usuario = await _finalizarLogin(u, tenant);
      return { ok: true, usuario };
    } catch (err) {
      setErro(err.message);
      return { ok: false };
    } finally {
      setCarregando(false);
    }
  }, [_finalizarLogin]);

  // ── Logout ────────────────────────────────────────────────
  const signOut = useCallback(async () => {
    await logLogout();
    await supabase.auth.signOut(); // invalida o JWT no servidor
    clearUsuario();
    clearTenant();
    clearTenantData();
  }, [clearUsuario, clearTenant, clearTenantData]);

  return { carregando, erro, signIn, signInComEmpresa, signOut };
}

export default useTenantAuth;
